/**
 * i18next bootstrap.
 *
 * The catalogs are the same files the Worker loads, so a key added for a page is
 * visible to both sides. The active language is kept in `localStorage` and, once
 * the user is signed in, mirrored onto their account by the profile page.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isLocale, type Locale } from '@shared/locales'

import en from '../../../locales/en.json'
import zhCN from '../../../locales/zh-CN.json'

export const LOCALE_STORAGE_KEY = 'toh.locale'

/**
 * Native name of each locale.
 *
 * A language is offered in itself rather than translated, so the mapping is not
 * part of the catalogs: an English interface still has to spell the Chinese
 * option `中文`.
 */
export const LOCALE_LABELS: Record<Locale, string> = {
  'zh-CN': '中文',
  en: 'English',
}

/** Locale stored in the browser, if any. */
export function storedLocale(): Locale | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const value = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return value && isLocale(value) ? value : null
  } catch {
    // Storage can be refused outright (private mode, blocked site data). The
    // choice is then simply not remembered; booting must not depend on it.
    return null
  }
}

export function storeLocale(locale: Locale): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // See `storedLocale`: a refused write must not break the language switch.
  }
}

/**
 * Locale the interface is currently rendering with.
 *
 * One definition for the whole client: pages and helpers read the active
 * language from the singleton rather than re-deriving it from `i18n.language`
 * (which is typed as `string`) at every call site.
 */
export function currentLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
}

/** Locale detected from the browser, falling back to the default. */
export function detectLocale(): Locale {
  const stored = storedLocale()
  if (stored) {
    return stored
  }
  if (typeof navigator !== 'undefined') {
    const candidate = navigator.languages?.[0] ?? navigator.language
    if (candidate) {
      if (isLocale(candidate)) {
        return candidate
      }
      if (candidate.toLowerCase().startsWith('zh')) {
        return 'zh-CN'
      }
      if (candidate.toLowerCase().startsWith('en')) {
        return 'en'
      }
    }
  }
  return DEFAULT_LOCALE
}

let initialized = false

/** Initialises i18next once; safe to call from any entry point. */
export function setupI18n(): typeof i18n {
  if (initialized) {
    return i18n
  }
  initialized = true

  void i18n.use(initReactI18next).init({
    resources: {
      'zh-CN': { translation: zhCN },
      en: { translation: en },
    },
    lng: detectLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    interpolation: { escapeValue: false },
    returnNull: false,
  })

  return i18n
}

/** Switches the active language and remembers the choice. */
export async function changeLocale(locale: Locale): Promise<void> {
  storeLocale(locale)
  await i18n.changeLanguage(locale)
}

export default i18n
